import { useEffect, useState, type ReactNode } from "react";
import { ApiError, type PartnerMeResponse } from "@dearbill/shared";
import { useApi } from "./useApi";
import { usePartnerSession } from "./usePartnerSession";
import { PartnerMeContextProvider } from "./PartnerMeContext";

/**
 * Loads `GET /api/partner/me` once for the authenticated app and publishes it
 * through `PartnerMeContext`. It mounts inside the auth gate, so it fetches only
 * with a session.
 *
 * A failure doesn't block the app. On a 401, `useApi` clears the session and
 * the redirect takes over. Any other failure leaves `me` null and sets `error`,
 * so consumers such as the org list show their own inline message while the top
 * bar falls back to "Partner" and sign-out still works.
 */
export function PartnerMeProvider({ children }: { children: ReactNode }): ReactNode {
  const api = useApi();
  const { session } = usePartnerSession();

  const [me, setMe] = useState<PartnerMeResponse | null>(null);
  const [settled, setSettled] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setMe(null);
    setSettled(false);
    setError(false);
    api<PartnerMeResponse>("/api/partner/me")
      .then((data) => {
        if (!cancelled) setMe(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A 401 already cleared the session in `useApi`, and the redirect takes over.
        if (err instanceof ApiError && err.status === 401) return;
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [api, session]);

  return (
    <PartnerMeContextProvider value={{ me, setMe, settled, error }}>
      {children}
    </PartnerMeContextProvider>
  );
}
